import { NavLink, Navigate, Outlet } from "react-router-dom";
import { LayoutDashboard, MessageSquareText, Send, ShieldCheck, Puzzle, Settings, LogOut, Loader2, ExternalLink, Upload, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/admin/recensioni", label: "Recensioni", icon: MessageSquareText, id: "reviews" },
  { to: "/admin/inviti", label: "Inviti", icon: Send, id: "invitations" },
  { to: "/admin/garanzia", label: "Garanzia", icon: ShieldCheck, id: "claims" },
  { to: "/admin/importa", label: "Importa", icon: Upload, id: "import" },
  { to: "/admin/integrazioni", label: "Integrazioni", icon: Puzzle, id: "integrations" },
  { to: "/admin/documentazione", label: "Documentazione", icon: BookOpen, id: "docs" },
  { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, id: "settings" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-150 ${isActive ? "bg-emerald-900 text-white" : "text-gray-700 hover:bg-gray-100"}`;

const Nav = () => items.map(({ to, label, icon: Icon, id, end }) => (
  <NavLink key={to} to={to} end={end} className={linkCls} data-testid={`admin-nav-${id}`}><Icon size={17} /> {label}</NavLink>
));

export default function AdminLayout() {
  const { user, logout } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return (
    <div className="min-h-screen bg-[#F9FAFB] lg:flex">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-gray-200 bg-white p-4 sticky top-0 h-screen print:hidden">
        <div className="px-3 py-3 mb-4">
          <img src="/img/rocard-logo-blue.png" alt="Rocard" className="h-9 w-auto" />
          <p className="text-xs text-gray-500 mt-1">Recensioni & Garanzia</p>
        </div>
        <nav className="space-y-1 flex-1"><Nav /></nav>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900" data-testid="admin-view-public">
          <ExternalLink size={15} /> Pagina pubblica
        </a>
        <div className="border-t border-gray-200 pt-3 mt-2">
          <p className="px-3 text-xs text-gray-500 truncate" data-testid="admin-user-email">{user.email}</p>
          <button onClick={logout} className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-red-700 hover:bg-red-50 transition-colors" data-testid="admin-logout-button">
            <LogOut size={16} /> Esci
          </button>
        </div>
      </aside>
      <header className="lg:hidden sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-gray-200 print:hidden">
        <div className="flex items-center justify-between px-4 h-14">
          <img src="/img/rocard-logo-blue.png" alt="Rocard" className="h-7 w-auto" />
          <button onClick={logout} className="text-sm text-red-700" data-testid="admin-logout-mobile">Esci</button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-2"><Nav /></nav>
      </header>
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}

export const PageHeader = ({ title, sub, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-gray-600 mt-1">{sub}</p>}
    </div>
    {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
  </div>
);
